/**
 * Inlines the canvas-runtime build into an iframe srcdoc as `data:` URLs.
 *
 * Blob URLs inherit the creator's origin and a null-origin sandbox can't
 * navigate to them. A real URL + CORS works on web but not Electron's
 * file:// without a custom protocol handler. data: URLs work everywhere.
 *
 * Requires `yarn build:canvas-runtime` before `yarn dev` / `yarn build`.
 */

const jsFiles: Record<string, string> = import.meta.glob('../../dist/*.js', {
  query: '?raw',
  import: 'default',
  eager: true,
});

import runtimeHtmlTemplate from '../../dist/runtime.html?raw';
import tailwindBrowserJs from '../vendor/tailwind-browser.js?raw';
// `?inline` returns a base64 data: URL string at host build time.
import interFontUrl from '../vendor/inter-variable.woff2?inline';
import sourceSerifFontUrl from '../vendor/source-serif-variable.woff2?inline';

const CHUNK_REF_REGEX = /\.\/(chunk-[^"'\s]+\.js)/g;

function basename(path: string): string {
  const i = path.lastIndexOf('/');
  return i === -1 ? path : path.slice(i + 1);
}

function findChunkRefs(content: string): string[] {
  const refs = new Set<string>();
  for (const match of content.matchAll(CHUNK_REF_REGEX)) {
    if (match[1]) {
      refs.add(match[1]);
    }
  }
  return Array.from(refs);
}

function toDataUrl(source: string): string {
  // btoa() chokes on multi-byte chars; encode bytes, not code units.
  const bytes = new TextEncoder().encode(source);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(
      ...bytes.subarray(i, Math.min(i + chunkSize, bytes.length)),
    );
  }
  return `data:text/javascript;base64,${btoa(binary)}`;
}

// Walks the chunk graph topologically: each file's `./chunk-X.js` refs are
// rewritten to data: URLs before the file itself is encoded.
function buildDataUrls(): Record<string, string> {
  const files: Record<string, string> = {};
  for (const [path, content] of Object.entries(jsFiles)) {
    files[basename(path)] = content;
  }

  const urlsByFilename: Record<string, string> = {};
  const pending = new Set(Object.keys(files));

  while (pending.size > 0) {
    let progressed = false;
    for (const name of pending) {
      const raw = files[name];
      if (raw == null) {
        continue;
      }
      const deps = findChunkRefs(raw);
      if (deps.some((d) => urlsByFilename[d] == null)) {
        continue;
      }
      const rewritten = raw.replace(CHUNK_REF_REGEX, (_match, dep: string) => {
        const url = urlsByFilename[dep];
        if (!url) {
          throw new Error(
            `missing data URL for chunk ${dep} (referenced by ${name})`,
          );
        }
        return url;
      });
      urlsByFilename[name] = toDataUrl(rewritten);
      pending.delete(name);
      progressed = true;
    }
    if (!progressed) {
      throw new Error(
        'canvas-runtime: circular chunk graph — cannot resolve data URLs',
      );
    }
  }

  return urlsByFilename;
}

// Injected into every srcdoc to forward file drags to the host via postMessage.
// Lives here (host-compiled) rather than client.ts because client.ts is in the
// pre-built dist/ bundle — changes there don't ship until the bundle rebuilds.
const DRAG_FORWARDING_SCRIPT = `
(function(){
  window.addEventListener('dragover',function(e){
    if(e.dataTransfer&&e.dataTransfer.types&&Array.prototype.indexOf.call(e.dataTransfer.types,'Files')!==-1){
      e.preventDefault();
      window.parent.postMessage({protocol:'canvas-runtime',message:{kind:'drag.over'}},'*');
    }
  });
  window.addEventListener('dragleave',function(e){
    if(e.relatedTarget===null){
      window.parent.postMessage({protocol:'canvas-runtime',message:{kind:'drag.end'}},'*');
    }
  });
  window.addEventListener('drop',function(e){
    var files=e.dataTransfer?Array.from(e.dataTransfer.files):[];
    if(files.length===0){return;}
    e.preventDefault();
    window.parent.postMessage({protocol:'canvas-runtime',message:{kind:'drag.drop',files:files}},'*');
  });
})();
`;

// When focus is inside the (null-origin) iframe, Cmd/Ctrl+P fires its keydown
// on the iframe document, never the parent — so the parent's shortcut handler
// can't see it. Cancel the native print here (which would otherwise slice the
// cross-origin frame) and forward the intent up; the host runs the
// snapshot-based print instead. Host-compiled (see DRAG_FORWARDING_SCRIPT).
const PRINT_KEY_FORWARDING_SCRIPT = `
(function(){
  window.addEventListener('keydown',function(e){
    if((e.metaKey||e.ctrlKey)&&!e.altKey&&!e.shiftKey&&(e.key==='p'||e.key==='P')){
      e.preventDefault();
      window.parent.postMessage({protocol:'canvas-runtime',message:{kind:'print.request'}},'*');
    }
  });
})();
`;

// The sandbox omits allow-popups, so plain clicks on <a target="_blank">
// are silently blocked; forward them to the host instead. Host-compiled
// (see DRAG_FORWARDING_SCRIPT).
const LINK_CLICK_FORWARDING_SCRIPT = `
(function(){
  document.addEventListener('click',function(e){
    if(e.defaultPrevented||e.button!==0||e.metaKey||e.ctrlKey||e.shiftKey||e.altKey){return;}
    var anchor=e.target&&e.target.closest?e.target.closest('a[href]'):null;
    if(!anchor){return;}
    var rawHref=anchor.getAttribute('href');
    if(!rawHref||rawHref.charAt(0)==='#'){return;}
    e.preventDefault();
    window.parent.postMessage({protocol:'canvas-runtime',message:{kind:'link.open',href:anchor.href}},'*');
  });
})();
`;

function buildSrcdoc(): string {
  const urlsByFilename = buildDataUrls();

  const runtimeUrl = urlsByFilename['runtime.js'];
  const lucideUrl = urlsByFilename['vendor-lucide.js'];
  const rechartsUrl = urlsByFilename['vendor-recharts.js'];

  if (!runtimeUrl || !lucideUrl || !rechartsUrl) {
    throw new Error(
      'canvas-runtime: missing expected output files. Did you run `yarn build:canvas-runtime`?',
    );
  }

  const tailwindUrl = toDataUrl(tailwindBrowserJs);

  const html = runtimeHtmlTemplate
    .replace(/__RUNTIME_JS_URL__/g, runtimeUrl)
    .replace(/__VENDOR_LUCIDE_URL__/g, lucideUrl)
    .replace(/__VENDOR_RECHARTS_URL__/g, rechartsUrl)
    .replace(/__TAILWIND_BROWSER_URL__/g, tailwindUrl)
    .replace(/__INTER_FONT_URL__/g, interFontUrl)
    .replace(/__SOURCE_SERIF_FONT_URL__/g, sourceSerifFontUrl);

  // Append the host-compiled scripts. They must run after the page is parsed
  // so they end up at the document level, where window events fire.
  const injectedScripts = `<script>${DRAG_FORWARDING_SCRIPT}</script><script>${PRINT_KEY_FORWARDING_SCRIPT}</script><script>${LINK_CLICK_FORWARDING_SCRIPT}</script>`;
  return html.includes('</body>')
    ? html.replace('</body>', `${injectedScripts}</body>`)
    : html + injectedScripts;
}

// Encoding the runtime is a multi-MB op; cache across mounts.
let cached: string | null = null;

export function packRuntimeSrcdoc(): string {
  if (cached == null) {
    cached = buildSrcdoc();
  }
  return cached;
}
